import * as React from "react";
import styled, { css } from "styled-components";
import * as moment from "moment-timezone";
import { useMappedState } from "redux-react-hook";

import { Text } from "src/library/components/Text";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { IStore } from "src/core/shared/store";
import {
  IPaymentCard,
  PaymentCardStatus
} from "src/modules/payment/api/payment.api";
import {
  expiryMonthNumberToString,
  paymentCardBrandTypeToHuman
} from "src/modules/payment/utils";

import PaymentMethodCardBase from "./PaymentMethodCardBase";

export interface PaymentMethodCardProps extends Pick<React.HTMLAttributes<HTMLDivElement>, "className" | "onClick"> {
  inline?: boolean;
  loading?: boolean;
  activeCardId?: IPaymentCard["id"];
  hasIssue?: boolean;
  defaultText?: string;
  s3BucketBaseUrl?: string;
  cardDetails?: IPaymentCard | null;
  backgroundColorType?: ColorType;
  hideDefaultLine?: boolean;
}

type ColorType = "light" | "dark";

interface StyledPaymentMethodCard {
  backgroundColorType: ColorType;
  defaultCard?: boolean;
}

/**
 * Payment method card
 */
const PaymentMethodCard: React.ReactFCWithChildren<PaymentMethodCardProps> = ({
  inline,
  loading,
  activeCardId,
  hasIssue,
  defaultText,
  cardDetails,
  s3BucketBaseUrl,
  backgroundColorType,
  hideDefaultLine,
  ...rest
}) => {
  const humanBrandName = React.useMemo(() => cardDetails ? paymentCardBrandTypeToHuman(cardDetails.brand) : "", [ cardDetails ]);
  const parsedExpiryMonth = React.useMemo(() => cardDetails ? expiryMonthNumberToString(cardDetails.expiryMonth) : "", [ cardDetails ]);

  const { currentDrop, terminology } = useMappedState(({ global, drops }: IStore) => ({
    currentDrop: drops.drop,
    terminology: global.settings?.terminology
  }));

  const numberOfAssignedDrops: number = React.useMemo(() => {
    return cardDetails?.branchPaymentCards ? cardDetails.branchPaymentCards.length : 0;
  }, [ cardDetails?.branchPaymentCards ]);

  const className = React.useMemo(() => {
    const classes = [];

    // Set a class if inline has been requested
    if (inline) {
      classes.push("inline");
    }

    // Set a class if hasIssue has been requested
    if (hasIssue) {
      classes.push("has-issue");
    }

    return classes.join(" ");
  }, [ inline, hasIssue ]);

  return (
    <>
      {loading || !cardDetails ? (
        <StyledPaymentMethodCardNew
          className={className}
          backgroundColorType={backgroundColorType as ColorType}
          {...rest}
        >
          {loading ? (
            <LoadingSpinner
              type="circular"
              size={24}
            />
          ) : (
            <Text className="default-text">
              {defaultText}
            </Text>
          )}
        </StyledPaymentMethodCardNew>
      ) : (
        <StyledPaymentMethodCardExisting
          className={className}
          backgroundColorType={backgroundColorType as ColorType}
          defaultCard={currentDrop?.defaultPaymentCardId === cardDetails.id}
          {...rest}
        >
          <div className="inline-column flex-05">
            {s3BucketBaseUrl && (
              <img
                className="card-brand-img"
                src={`${s3BucketBaseUrl}/public/assets/payment-methods/${cardDetails.brand}.png`}
                alt={humanBrandName}
              />
            )}
          </div>

          <div className="inline-column card-number flex-2">
            <Text>
              <Text>
                {`${humanBrandName} •••• ${cardDetails.last4}`}
              </Text>
            </Text>
          </div>

          <div className="inline-column flex-1">
            <Text className="card-expiry-date">
              {`Expires ${parsedExpiryMonth}/${cardDetails.expiryYear}`}
            </Text>
          </div>

          <div className="inline-column flex-05 card-state">
            {activeCardId && activeCardId === cardDetails.id ? (
              <Text className="card-active">
                Current
              </Text>
            ) : (
              <>
                {cardDetails.status !== PaymentCardStatus.ACTIVE ? (
                  <Text className="card-expired">
                    {cardDetails.status}
                  </Text>
                ) : (
                  <>
                    {hasIssue && (
                      <Text className="card-has-issue">
                        Error
                      </Text>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {!inline && (
            <div className="dynamic-data">
              <>
                {currentDrop && (
                  <div className="default-card-container">
                    <div>
                      <Text className="default-card-text">
                        {currentDrop.defaultPaymentCardId === cardDetails.id && !hideDefaultLine ? "Default Payment Method" : ""}
                      </Text>
                    </div>

                    <div>
                      <Text className="default-card-text">
                        {`Assigned to ${numberOfAssignedDrops === 0 ?
                          `no ${terminology?.drop.plural.text}.` :
                          `${numberOfAssignedDrops} ${terminology?.drop[ numberOfAssignedDrops === 1 ? "singular" : "plural" ].text}`}`}
                      </Text>
                    </div>
                  </div>
                )}

              </>
            </div>
          )}

          <div className="card-last-used-container">
            <Text className="card-last-used">
              {`Last used ${cardDetails.lastUsed ? moment.utc(cardDetails.lastUsed).local().format("MM/YYYY") : "never"}`}
            </Text>
          </div>
        </StyledPaymentMethodCardExisting>
      )}
    </>
  );
};

export default PaymentMethodCard;

PaymentMethodCard.defaultProps = {
  defaultText: "Payment method",
  backgroundColorType: "dark"
};

const cardWidth = 300;

const StyledPaymentMethodCardNew = styled(PaymentMethodCardBase)<StyledPaymentMethodCard>`
  ${({ theme }) => css`
    align-items: center;
    background-color: ${theme.colors.white};
    width: ${cardWidth}px;

    .default-text {
      transition: ${theme.transition.quickest};
      color: ${theme.fonts.colors.secondary};
      font-size: ${theme.fonts.sizes.h4};
    }

    &:hover {
      border-color: ${theme.colors.primaryDark};

      .default-text {
        color: ${theme.fonts.colors.primary};
      }
    }

    .inline {
      width: 100%;
    }
  `};
`;

const StyledPaymentMethodCardExisting = styled(PaymentMethodCardBase)<StyledPaymentMethodCard>`
  ${({
    theme,
    onClick,
    backgroundColorType,
    defaultCard
  }) => css`
    background-color: ${backgroundColorType === "dark" ? theme.colors.lightestGrey : theme.colors.white};
    border-color: ${defaultCard ? "inherit" : "transparent"};
    border-style: solid;
    width: ${cardWidth}px;

    &:hover {
      border-color: ${onClick ? theme.colors.primaryDark : defaultCard ? "inherit" : "transparent"};
    }

    .card-brand-img {
      display: block;
      width: 3.4rem;
      height: auto;
    }

    .card-number {
      padding-top: 1.4rem;

      @media (min-width: 768px) {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
      }

      span {
        display: block;
        color: ${theme.colors.darkGrey};
        font-size: 1.6rem;
        font-weight: ${theme.fonts.weights.bold};
      }
    }

    .card-expiry-date,
    .card-last-used-container .card-last-used {
      color: ${theme.fonts.colors.secondary};
    }

    .card-expiry-date {
      padding-top: 0.2rem;
      font-size: ${theme.fonts.sizes.h5};
    }

    .card-state {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin: 1.2rem;

      .card-active,
      .card-expired,
      .card-has-issue {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0.3rem 0.6rem;
        height: 100%;
        font-size: ${theme.fonts.sizes.h6};
        font-weight: ${theme.fonts.weights.bold};
        text-transform: uppercase;
        line-height: 1em;
        letter-spacing: 0.1em;
        border-radius: 0.4rem;
      }
  
      .card-active {
        color: ${theme.fonts.colors.white};
        background-color: ${theme.colors.primaryDark};
      }
  
      .card-has-issue {
        color: ${theme.fonts.colors.white};
        background-color: ${theme.colors.error};
      }
  
      .card-expired {
        color: #600701;
        background-color: #FFBBBB;
      }
    }

    .card-last-used-container {
      display: flex;
      align-items: center;

      .card-last-used {
        padding-top: 0.2rem;
        font-size: ${theme.fonts.sizes.h6};
        font-weight: ${theme.fonts.weights.bold};
        line-height: 1em;
        text-transform: uppercase;
      }
    }

    .default-card-container {
      margin-top: 1rem;

      .default-card-text {
        color: ${theme.fonts.colors.secondary};
        padding-top: 0.2rem;
        font-size: ${theme.fonts.sizes.h6};
        font-weight: ${theme.fonts.weights.bold};
        line-height: 1em;
        text-transform: uppercase;
      }
    }

    &.has-issue {
      border-color: ${theme.colors.error};
    }

    &.inline {
      flex-direction: row;
      align-items: center;
      width: 100%;

      .inline-column {
        padding: 0;
      }

      .inline-column:is(.flex-2) {
        flex: 2;
      }

      .inline-column:is(.flex-05) {
        flex: 0.5;
      }

      .inline-column:is(.flex-1) {
        flex: 1;
      }
    }
  `};
`;
